/***** frontend/src/contexts/AuthContext.js *****/
import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api';
import jwtDecode from 'jwt-decode';
import { io } from 'socket.io-client';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser]               = useState(null);          // 当前用户
  const [loading, setLoading]         = useState(true);          // 是否加载中
  const [socket, setSocket]           = useState(null);          // socket.io 实例
  const [globalTurnCount, setGlobalTurnCount] = useState(0);     // 示例：全局转数
  const [jackpotMap, setJackpotMap]   = useState({});            // 各设备开奖状态
  const [messages, setMessages]       = useState([]);            // 新增：全局消息列表
  const [unreadCount, setUnreadCount] = useState(0);             // 新增：未读消息计数

  /* ================================================================
   * 1) 首次挂载：若本地有 token 就尝试复用
   * ==============================================================*/
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try { jwtDecode(token); }                         // 校验格式
    catch (e) {
      console.error('[AuthContext] Token 解析失败:', e);
      localStorage.removeItem('token');
      setLoading(false);
      return;
    }
    fetchUserInfo(token);                            // 拉用户 & 建 socket
  }, []);

  /* ================================================================
   * 2) 拉取用户信息
   * ==============================================================*/
  const fetchUserInfo = async (token) => {
    try {
      const { data } = await api.get('/api/users/userinfo');
      setUser(data);
      initSocket(token);
      return data;
    } catch (err) {
      console.error('[AuthContext] 获取用户信息失败:', err);
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /* ================================================================
   * 3) 建立 / 复用 socket.io 连接
   * ==============================================================*/
  const initSocket = (token) => {
    if (socket) { socket.off(); socket.disconnect(); }
    // 使用后端完整地址，确保 WebSocket 直连
    const backendUrl = process.env.REACT_APP_SOCKET_URL || api.defaults.baseURL;
    console.debug('[AuthContext] initSocket: connecting to', backendUrl);
    const newSocket = io(backendUrl, {
      path: '/socket.io',
      transports: ['polling'],
      auth: { token }
    });
    // 全部事件监听调试
    newSocket.onAny((event, payload) => console.debug('[AuthContext] event', event, payload));
    newSocket.on('connect', () => console.debug('[AuthContext] socket connected', newSocket.id));
    newSocket.on('connect_error', err => console.error('[AuthContext] socket connect_error', err));
    newSocket.on('disconnect', reason => console.debug('[AuthContext] socket disconnected', reason));
    // 实时数据
    newSocket.on('pointsUpdate', ({ newPoints }) => setUser(u => u ? { ...u, currentPoints: newPoints } : u));
    newSocket.on('silverUpdate', ({ silverCoins }) => setUser(u => u ? { ...u, silverCoins: Number(silverCoins.toFixed(1)) } : u));
    // 示例事件
    newSocket.on('turnUpdate', ({ turnCount }) => setGlobalTurnCount(turnCount));
    newSocket.on('jackpotUpdate', payload => setJackpotMap(m => ({ ...m, [payload.deviceId]: payload })));
    newSocket.on('newMessage', msg => setMessages(m => [...m, msg]));
    setSocket(newSocket);
  };

  /* ================================================================
   * 监听 socket 新消息
   * ==============================================================*/
  useEffect(() => {
    if (!socket || !user) return;
    const handler = (msg) => {
      if (String(msg.from) === String(user._id) || String(msg.to) === String(user._id)) {
        setMessages(prev => {
          if (prev.some(m => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
        if (!window.customerServiceModalOpen && String(msg.to) === String(user._id)) {
          setUnreadCount(c => c + 1);
        }
      }
    };
    socket.on('newMessage', handler);
    return () => socket.off('newMessage', handler);
  }, [socket, user, setUnreadCount]);

  

  /* ================================================================
   * 4) 登录
   * ==============================================================*/
  const login = async (username, password) => {
    try {
      const { data } = await api.post('/api/users/login', { username, password });
      if (!data.token) return { success:false, message:'后端未返回 token' };

      localStorage.setItem('token', data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;

      const freshUser = await fetchUserInfo(data.token);
      return freshUser
        ? { success:true, user:freshUser }
        : { success:false, message:'获取用户信息失败' };
    } catch (err) {
      console.error('[AuthContext] 登录失败:', err);
      return { success:false, message:err.response?.data?.message || '登录失败' };
    }
  };

  /* ================================================================
   * 5) 管理员登录
   * ==============================================================*/
  const adminLogin = async (username, password) => {
    try {
      const { data } = await api.post('/api/users/adminLogin', { username, password });
      if (!data.token) return { success:false, message:'后端未返回 token' };

      localStorage.setItem('token', data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;

      const freshUser = await fetchUserInfo(data.token);
      if (!freshUser || freshUser.role !== 'admin') {
        logout();
        return { success:false, message:'该账号不是管理员' };
      }
      return { success:true, user:freshUser };
    } catch (err) {
      console.error('[AuthContext] 管理员登录失败:', err);
      return { success:false, message:err.response?.data?.message || '管理员登录失败' };
    }
  };

  /* ================================================================
   * 6) 登出
   * ==============================================================*/
  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    if (socket) { socket.off(); socket.disconnect(); setSocket(null); }
    setUser(null);
    setLoading(false);
  };

  /* ================================================================
   * 7) 刷新用户信息
   * ==============================================================*/
  const refreshUserInfo = async () => {
    try {
      const { data } = await api.get('/api/users/userinfo');
      setUser(data);
      return data;
    } catch (err) {
      console.error('[AuthContext] 刷新用户信息失败:', err);
      return null;
    }
  };

  /* ================================================================
   * 8) Context Provider 输出
   * ==============================================================*/
  return (
    <AuthContext.Provider value={{
      user,
      loading,
      socket,
      messages,         // 新增
      setMessages,      // 新增
      globalTurnCount,
      jackpotMap,
      unreadCount,      // 新增
      setUnreadCount,   // 新增
      login,
      adminLogin,
      logout,
      refreshUserInfo   // 新增：刷新用户信息方法
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);